import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { ModeStandbyOutlined, AttachMoneyOutlined, WorkOutlineOutlined } from "@mui/icons-material";

import {
  DELETE_CONTRACT,
  DELETE_SUBCONTRACTOR_TRANSACTION,
  EDIT_CONTRACT,
  EDIT_SUBCONTRACTOR_TRANSACTION,
  EXPORT_SUBCONTRACTOR_TRANSACTION,
  VIEW_SUBCONTRACTOR_LEDGER_MODE_STATEMENT,
} from "../../constants";
import { getLedgerTableColumns, getStatementTableColumns } from "../../pages/subcontractors/constants";
import { AnalyticsService, FinanceService, ProjectService, PurchaseService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { CardLayout, DeleteModal, LedgerModeToggle, MTable } from "../v3";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const SubcontractorStatement = ({
  name = "Subcontractor",
  subcontractorId = null,
  onSuccess = () => {},
  reloadData = 0,
}) => {
  const [projects, setProjects] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [projectsOfTransaction, setProjectsOfTransaction] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    isLedgerMode: isSavedLedgerMode,
    setLedgerMode: setIsLedgerMode,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "statement",
    defaults: {
      filter: "ALL",
      dropdowns: { projectId: "", projectName: "" },
      sortModel: [{ field: "date", sort: "desc" }],
    },
    filterValues: ["ALL", "contract", "expense"],
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: "", recordId: null });

  const hasEditContract = useHasPermission(EDIT_CONTRACT);
  const hasDeleteContract = useHasPermission(DELETE_CONTRACT);
  const hasEditTransaction = useHasPermission(EDIT_SUBCONTRACTOR_TRANSACTION);
  const hasDeleteTransaction = useHasPermission(DELETE_SUBCONTRACTOR_TRANSACTION);
  const hasExportTransaction = useHasPermission(EXPORT_SUBCONTRACTOR_TRANSACTION);
  const hasLedgerAccess = useHasPermission(VIEW_SUBCONTRACTOR_LEDGER_MODE_STATEMENT);
  // A remembered ledger toggle never outlives the permission.
  const isLedgerMode = hasLedgerAccess && isSavedLedgerMode;
  const selectedProjectId = dropdowns.projectId;
  // The name is kept with the id: project options load 10 at a time, so a restored project may not be loaded yet.
  const setSelectedProjectId = (projectId) =>
    setDropdowns({
      projectId,
      projectName: projects.find((project) => project.id === projectId)?.name ?? "",
    });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (transactionFilters = {}) => {
      setLoading(true);
      try {
        const response = isLedgerMode
          ? await AnalyticsService.getSubcontractorLedger({ ...transactionFilters, vendorId: subcontractorId })
          : await AnalyticsService.getSubcontractorAnalytics({ ...transactionFilters, vendorId: subcontractorId });
        setTransactions(response?.data?.rows || []);
        setTotalCount(response?.data?.count || 0);

        // gets the unique project ids from the transaction response to get the project names
        const projectIds = Array.from(new Set(response?.data?.rows?.map((item) => item.projectId)));
        if (projectIds.length > 0) {
          const projectResponse = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjectsOfTransaction(projectResponse?.data?.rows);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        toast.error("Failed to fetch transactions");
      } finally {
        setLoading(false);
      }
    },
    [subcontractorId, isLedgerMode],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      ...(!isLedgerMode && {
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        ...(selectedFilter !== "ALL" && { type: selectedFilter }),
        ...(selectedProjectId && { projectId: selectedProjectId }),
        search: searchText,
      }),
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, selectedProjectId, searchText, dateRange, reloadData, isLedgerMode]);

  const handleLedgerModeToggle = () => {
    setIsLedgerMode((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };
  const fetchProjects = async (page = 1) => {
    if (projectLoading) {
      return;
    }
    setProjectLoading(true);
    try {
      const response = await ProjectService.getProjectList({
        page,
        limit: 10,
        includeDeleted: true,
      });
      const rows = response?.data?.rows || [];

      setProjects((prev) => (page === 1 ? rows : [...prev, ...rows]));

      setHasMoreProjects(rows.length === 10);
      setProjectPage(page);
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(1);
  }, []);
  const handleProjectScroll = () => {
    if (projectLoading || !hasMoreProjects) {
      return;
    }

    fetchProjects(projectPage + 1);
  };
  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Use start/end-of-day boundaries to avoid dropping same-day records.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[selectedFilter] || `Contracts & Payments : ${name}`;
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={pdfTitle} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: pdfTitle,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleProjectFilterChange = (value) => {
    setSelectedProjectId(value ?? "");
    setPaginationState((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const handleProjectFilterClear = () => {
    setSelectedProjectId("");
    setPaginationState((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const onViewClick = (params, event) => {
    if (params.row.type === "expense") navigate(`/subcontractor/${subcontractorId}/payment/${params.row.transactionId}`);
    else if (params.row.type === "contract") navigate(`/subcontractor/${subcontractorId}/contract/${params.row.transactionId}`);
  };

  const openDeleteModal = (record) => {
    setDeleteModal({
      isOpen: true,
      type: record?.type,
      recordId: record?.transactionId,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      type: "",
      recordId: null,
    });
  };

  const handleEditTransaction = (transactionDetails) => {
    navigate(`/transaction/edit/${transactionDetails.transactionId}?subcontractorId=${subcontractorId}`);
  };
  const handleEditContract = (contractDetails) => {
    navigate(`/contracts/edit/${contractDetails.transactionId}`);
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === "expense") await FinanceService.deleteExpense(deleteModal.recordId);
      else if (deleteModal.type === "contract") await PurchaseService.deleteContract(deleteModal.recordId);

      onSuccess?.();
      toast.success(`${deleteModal.type === "expense" ? "transaction" : "contract"} deleted successfully`);
    } catch (error) {
      toast.error(`Failed to delete ${deleteModal.type}`);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (entityData, actionType) => {
    switch (actionType) {
      case "edit":
        entityData.type === "expense" ? handleEditTransaction(entityData) : handleEditContract(entityData);
        break;
      case "delete":
        openDeleteModal(entityData);
        break;
      default:
        toast.warning("Invalid action");
        return;
    }
  };

  const columnHeaders = isLedgerMode
    ? getLedgerTableColumns(handleActions, hasEditContract, hasDeleteContract, hasEditTransaction, hasDeleteTransaction)
    : getStatementTableColumns(
        handleActions,
        hasEditContract,
        hasDeleteContract,
        hasEditTransaction,
        hasDeleteTransaction,
        selectedFilter,
      );
  const dropdownFilters = [
    {
      key: "projectId",
      label: "Project",
      value: selectedProjectId,
      onChange: handleProjectFilterChange,
      onClear: handleProjectFilterClear,
      options: [
        ...projects.map((project) => ({
          label: project.name,
          value: project.id,
        })),
        ...(selectedProjectId && !projects.some((project) => project.id === selectedProjectId)
          ? [{ label: dropdowns.projectName, value: selectedProjectId }]
          : []),
      ],
      multiple: false,
      onScrollEnd: handleProjectScroll,
      loading: projectLoading,
      disabled: isLedgerMode,
    },
  ];

  const ledgerModeToggle = hasLedgerAccess ? (
    <LedgerModeToggle isLedgerMode={isLedgerMode} onToggle={handleLedgerModeToggle} />
  ) : null;
  const filterOptions = [
    { label: "All", value: "ALL", icon: <ModeStandbyOutlined /> },
    { label: "Contracts", value: "contract", icon: <WorkOutlineOutlined /> },
    { label: "Payment", value: "expense", icon: <AttachMoneyOutlined /> },
  ];

  const pdfTitleMap = {
    ALL: `Contracts & Payments : ${name}`,
    contract: `Contracts : ${name}`,
    expense: `Payments : ${name}`,
  };

  const rows = transactions
    ? transactions.map((transaction, index) => {
        const project = projectsOfTransaction.find((project) => project.id === transaction.projectId);
        return {
          sl: index + 1 + paginationState.page * paginationState.pageSize,
          id: index + 1 + paginationState.page * paginationState.pageSize,
          transactionId: transaction?.id,
          date: transaction?.date
            ? moment(transaction.date).format("DD MMM YYYY")
            : moment(transaction?.startDate).format("DD MMM YYYY"),
          projectName: project?.name,
          description: transaction?.description || transaction?.title,
          amount: transaction?.type === "contract" ? transaction?.amount : null,
          payment: transaction?.type === "expense" ? transaction?.amount : null,
          balance: transaction?.balance,
          subcontractorId: transaction?.subcontractorId,
          projectId: transaction?.projectId,
          type: transaction?.type,
          toId: transaction?.toId,
        };
      })
    : [];

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          startAction={ledgerModeToggle}
          dropdownFilters={dropdownFilters}
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          disableFilterButton={isLedgerMode}
          // Search data
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          disableSearchBar={isLedgerMode}
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportTransaction}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          disableSorting={isLedgerMode}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Are you sure you want to delete this ${deleteModal.type === "expense" ? "transaction" : "contract"}?`}
      />
    </>
  );
};
